/**
 * app/components/GeneralNotification.tsx
 * --------------------------------------
 * What: Dismissible banner for system-wide messages driven by remote config.
 * Why: Allows timely announcements without shipping a new app version.
 */
import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../lib/ThemeContext';

interface GeneralNotificationProps {
  message: string;
}

export default function GeneralNotification({ message }: GeneralNotificationProps) {
  const { theme, themeType } = useTheme();

  const glowStyle = themeType === 'blueNeon'
    ? (Platform.OS === 'web'
        ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any
        : {
            shadowColor: theme.accent,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.5,
            shadowRadius: 6,
            elevation: 6,
          })
    : {};

  if (!message) return null; // Nothing to show

  return (
    <View style={[styles.container, { backgroundColor: theme.card, borderColor: theme.border }, glowStyle]}>
      <View style={styles.headerRow}>
        <Ionicons name="information-circle" size={18} color={theme.accent} style={{ marginRight: 6 }} />
        <Text style={[styles.headerText, { color: theme.accent }]}>Notification</Text>
        <Ionicons name="information-circle" size={18} color={theme.accent} style={{ marginLeft: 6 }} />
      </View>
      <Text style={[styles.message, { color: theme.secondaryText }]}>{message}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  headerText: {
    fontSize: 17,
    fontWeight: '500',
  },
  message: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
  },
}); 